export const FunnelPeriods = {
  '1w': '1 Week',
  '2w': '2 Weeks',
  '1m': '1 Month',
  '1q': '1 Quarter',
  'all': 'All Time',
} as const

export type FunnelPeriod = keyof typeof FunnelPeriods

export type InvoicingFunnelStage = {
  stage: string
  count: number
  order: number
  invoiceVolume: number
}

export type AdoptionDepthHeadlines = {
  adoptingTenantCount: number
  invoiceCountSharePct: number
  dollarVolumeSharePct: number
}

export type AdoptionDepthBucket = {
  label: string
  order: number
  tenantCount: number
}

export type AdoptionDepthData = {
  headlines: AdoptionDepthHeadlines
  invoiceCountBuckets: AdoptionDepthBucket[]
  dollarVolumeBuckets: AdoptionDepthBucket[]
  generatedAt: string
}

export const PaymentsByJobTypeBuckets = {
  DesignBuild: 'Design/Build',
  Maintenance: 'Maintenance',
  ServiceTicket: 'Service Ticket',
  Unknown: 'Unknown',
} as const

export type PaymentsByJobTypeBucketKey = keyof typeof PaymentsByJobTypeBuckets

export type PaymentsByJobTypeBucket = {
  bucket: PaymentsByJobTypeBucketKey
  order: number
  paidInvoiceCount: number
  paidInvoiceVolume: number
  tenantCount: number
}

export type PaymentsByJobTypeTotals = {
  paidInvoiceCount: number
  paidInvoiceVolume: number
}

export type PaymentsByJobTypeData = {
  buckets: PaymentsByJobTypeBucket[]
  totals: PaymentsByJobTypeTotals
  generatedAt: string
}

export type InvoicingFunnelData = {
  adoptionFunnel: {
    stages: InvoicingFunnelStage[]
    generatedAt: string
  }
  adoptionDepth?: AdoptionDepthData
  paymentsByJobType?: PaymentsByJobTypeData
}

export type ComputedStage = {
  stage: string
  count: number
  invoiceVolume: number
  stepConversion: number | null
  overallConversion: number | null
  dropoff: number | null
}

export type FunnelSummary = {
  totalCount: number
  approvedCount: number
  approvedPercent: number | null
  payingCount: number
  payingPercent: number | null
  totalDropoff: number
}

export type BucketSegment = AdoptionDepthBucket & { pct: number }

export type PaymentsByJobTypeRow = PaymentsByJobTypeBucket & {
  label: string
  countPct: number | null
  volumePct: number | null
}

export const computePaymentsByJobTypeRows = (
  buckets: PaymentsByJobTypeBucket[],
  totals: PaymentsByJobTypeTotals
): PaymentsByJobTypeRow[] => {
  const sorted = [...buckets].sort((a, b) => a.order - b.order)
  return sorted.map(b => ({
    ...b,
    label: PaymentsByJobTypeBuckets[b.bucket] ?? b.bucket,
    countPct: totals.paidInvoiceCount > 0
      ? (b.paidInvoiceCount / totals.paidInvoiceCount) * 100
      : null,
    volumePct: totals.paidInvoiceVolume > 0
      ? (b.paidInvoiceVolume / totals.paidInvoiceVolume) * 100
      : null,
  }))
}

export const computeBucketSegments = (buckets: AdoptionDepthBucket[]): BucketSegment[] => {
  const sorted = [...buckets].sort((a, b) => a.order - b.order)
  const total = sorted.reduce((sum, b) => sum + b.tenantCount, 0)
  return sorted.map(b => ({ ...b, pct: total > 0 ? (b.tenantCount / total) * 100 : 0 }))
}

export const computeStages = (stages: InvoicingFunnelStage[]): ComputedStage[] => {
  const sorted = [...stages].sort((a, b) => a.order - b.order)
  const topCount = sorted[0]?.count ?? 0
  return sorted.map((s, i) => {
    const prevCount = i > 0 ? sorted[i - 1].count : null
    return {
      stage: s.stage,
      count: s.count,
      invoiceVolume: s.invoiceVolume,
      stepConversion: prevCount !== null && prevCount > 0
        ? (s.count / prevCount) * 100
        : null,
      overallConversion: i > 0 && topCount > 0
        ? (s.count / topCount) * 100
        : null,
      dropoff: prevCount !== null ? prevCount - s.count : null,
    }
  })
}

export const computeSummary = (stages: ComputedStage[]): FunnelSummary => {
  const totalCount = stages[0]?.count ?? 0
  const approvedCount = stages.find(s => s.stage === 'Application Approved')?.count ?? 0
  const payingCount = stages[stages.length - 1]?.count ?? 0
  return {
    totalCount,
    approvedCount,
    approvedPercent: totalCount > 0 ? (approvedCount / totalCount) * 100 : null,
    payingCount,
    payingPercent: totalCount > 0 ? (payingCount / totalCount) * 100 : null,
    totalDropoff: totalCount - payingCount,
  }
}
